import React, { useContext, useState, useEffect } from "react";
import styled from "styled-components";
import * as d3 from "d3";
import Request from "common/Request";
import ColumnsGrid from "common/layouts/ColumnsGrid";
import ActionsContext from "common/ActionsContext";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import { retrieveRange, isInsideRange } from "common/api";
import RangeSliderInput from "common/RangeSliderInput";
import NVStackedAreaChart, {
  tooltipContentGenerator,
} from "common/graphs/NVStackedAreaChart";
import { PERIOD_TO_HOURS, periodScopeChoicesWith3Months, categoryChoices } from "common/constants";
import { parseShareInSpeed } from "../DPI/api";
import { getServiceFields, servicesSpeedAsFields } from "../SubscriberDetail/api";
import CategoryDetailsModal from "./CategoryDetailsModal";
import SubscriberGroupLatency from "./SubscriberGroupLatency";
import DPISpeed from "./SubscriberGroupDPISpeed";
import { safeStr } from "common/api";

const Wrapper = styled.div`
  width: 100%;
  .latency-charts-wrapper{
    padding: 0 20px;
     &.time-evolution {
      margin-top: 10px;
    }
  }
  .export-dpi-latencies{
    position: relative;
    right: 20px;
    top: -22px;
  }
`;

const Header = styled.div`
  padding: 10px 20px !important;
`;

const HeaderTitle = styled.div`
  width: 100%;
  align-items: center;
  justify-content: space-between;
  display: flex;

  h4 {
    margin-bottom: 0px;
  }

  .context-menu.refresh {
    .material-icons {
      font-size: 20px;
      color: #999;
    }
  }
`;

const WrapperContextMenu = styled.div`
  padding: 0 20px;
`;

const Title = styled.div`
  margin-bottom: 10px;
`;

const SelectWrapper = styled.div`
  width: 150px;
`;

const ChartContainer = styled.div`
  width: 100%;
`;
const DPIChartContainer = styled.div`
  width: 100%;
`;

const WrapperSpeed = styled.div`
margin: 0 20px;

.header-dropdown{
top: 30px !important;
}
`;

const RangeContainer = styled.div``;

const ContextMenuWrapper = styled.div`
  display: block;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const _directionChoices = [
  { value: "all", label: "Both." },
  { value: "uplink", label: "Uplink" },
  { value: "downlink", label: "Downlink" },
];

const ContextMenu = ({period, setPeriod, categories, setCategories, direction, setDirection}) => {
  return (
    <ContextMenuWrapper>
      <ColumnsGrid
        columns={3}
        className={"no-print"}
        minWidth="15rem"
        rowGap={"0"}
      >
        <SelectInputWithIcon
          title="Date Range"
          name="Date Range"
          icon="date_range"
          selected={period}
          onChange={({target})=> setPeriod(target.value)}
          options={periodScopeChoicesWith3Months}
        />
        <SelectInputWithIcon
          title="Categories"
          name="Categories"
          icon="format_line_spacing"
          selected={categories}
          onChange={({target})=> setCategories(target.value)}
          options={categoryChoices}
        />
        <SelectInputWithIcon
          title="Direction"
          name="Direction"
          icon="import_export"
          selected={direction}
          onChange={({target})=> setDirection(target.value)}
          options={_directionChoices}
        />
      </ColumnsGrid>
    </ContextMenuWrapper>
  );
};

const parseDate = d3.timeParse("%Y-%m-%dT%H:%M:%S");

const mbToGb = ({ total, dpi }) => ({
  total: (total / 1000).toFixed(2),
  dpi: (dpi / 1000).toFixed(2),
});

const sumTotalAndDPI = (items) =>
  items.reduce(
    (prev, { total, dpi }) => ({
      total: prev.total + total,
      dpi: prev.dpi + dpi,
    }),
    { total: 0, dpi: 0 }
  );

const TotalVolumeVsDPI = ({ total = "--", dpi = "--" }) => {
  return (
    <div className="align-left">
      <h4 className="chart-title">
        Total volume: {total} GB - Dpi sample: {dpi} GB
      </h4>
    </div>
  );
};

const periodInitMap = {
  oneDay: "Day",
  oneWeek: "Week",
  oneMonth: "Month",
  threeMonths: "Month",
}

const HOURS_INTERVAL_MAP = {
  24: 60,
  168: 360,
  720: 1440,
  2160: 4320,
};

const SubscriberGroupDPI = ({ subscriberGroup, period: periodInit }) => {
  const [request, setRequest] = useState(null);
  const [initialRange, setInitialRange] = useState();
  const [period, setPeriod] = useState(periodInitMap[periodInit]);
  const [categories, setCategories] = useState(10);
  const [direction, setDirection] = useState("all");
  const [range, setRange] = useState();
  const [category, setCategory]=useState(null);
  const actions = useContext(ActionsContext);
  const hours = PERIOD_TO_HOURS[period];

  const doRequest = (period) => {
    return retrieveRange(hours).then(({ from, to }) => {
      setInitialRange({ from, to });
      return ifCl
        .run(
          `show statistics dpi speed group ${safeStr(subscriberGroup)} hours ${hours} interval ${HOURS_INTERVAL_MAP[hours]} categories ${categories -1} direction ${direction} interval-speed 10`, { timeout: DPI_STATS_TMOUT_MS }
        )
        .then(parseShareInSpeed)
        .then((response) => {
          return { items: response };
        });
    });
  };

  const doLoad = () => {
    setRange(undefined);
    setRequest(doRequest(period));
  };

  useEffect(() => {
    doLoad();
    return actions.recv("do-load-details-dpi", () =>
      doLoad()
    );
  }, [period, direction, categories]);

  return (
    <div className="card">
      <Header className="header block-header">
        <HeaderTitle>
          <Title>
            <h4>DPI Application Breakdown Over Time</h4>
            <small>
              <code>right-click</code> in the area for a DPI category inside the
              chart or in the labels for each category above the chart
            </small>
          </Title>
          <div className="context-menu refresh">
            <a
              onClick={() => actions.send("do-load-details-dpi")}
              data-toggle="cardloading"
              data-loading-effect="pulse"
              title="Refresh"
            >
              <i className="material-icons">refresh</i>
            </a>
          </div>
        </HeaderTitle>
      </Header>
      <WrapperContextMenu>
        <ContextMenu
          period={period}
          setPeriod={setPeriod}
          direction={direction}
          setDirection={setDirection}
          categories={categories}
          setCategories={setCategories}
        />
      </WrapperContextMenu>
      <div className="body">
        <Request during={request}>
          {({ items }) => {
            const selectedRange = range || initialRange;
            return (
              <DPIChartContainer>
                <CategoryDetailsModal
                  category={category}
                  setCategory={setCategory}
                  period={period}
                  direction={direction}
                  categories={categories}
                  subscriberGroup={subscriberGroup}
                />
                <ChartContainer className="group chart-container">
                  <NVStackedAreaChart
                    className="dpi-usage-chart"
                    items={servicesSpeedAsFields(
                      items.filter(isInsideRange(selectedRange))
                    )}
                    xAxisFormat={d3.timeFormat("%m/%d %H:%M")}
                    xField="time"
                    fields={getServiceFields(items)}
                    tooltipContentGenerator={tooltipContentGenerator}
                    yAxisUnits="Mbps"
                    yAxisTopGap={0.05}
                    onSeriesInspect={({ color, name }) =>
                      setCategory({ name, color })
                    }
                  />
                  <TotalVolumeVsDPI {...mbToGb(sumTotalAndDPI(items))} />
                </ChartContainer>
                <RangeContainer>
                  {initialRange && (
                    <RangeSliderInput
                      {...initialRange}
                      onChange={(value) => setRange(value)}
                    />
                  )}
                </RangeContainer>
              </DPIChartContainer>
            );
          }}
        </Request>
      </div>
      <WrapperSpeed>
        <DPISpeed hours={hours} subscriberGroup={subscriberGroup}/>
      </WrapperSpeed>
      <div className="dpi-fixed">
        <Wrapper className="dpi-fixed-content">
          <SubscriberGroupLatency
            range={range || initialRange}
            period={period}
            direction={direction}
            categories={categories}
            subscriberGroup={subscriberGroup}
          />
        </Wrapper>
      </div>
    </div>
  );
};

export default SubscriberGroupDPI;
